import { useLingui } from "@lingui/react/macro";
import { sessionTone, startedLabel } from "@/lib/multi-view";
import type { CookingSession } from "@/types/cooking-session";
import { SessionActions } from "./SessionActions";
import { ChefLabel } from "./SessionFacts";
import { SessionStatusPill } from "./SessionStatusPill";
import { SessionTimer } from "./SessionTimer";

interface SessionTopBarProps {
  session: CookingSession;
  seconds: number;
  onError: (message: string) => void;
}

/**
 * The strip over a recipe shown full size (Tabs view and the spotlight): the
 * big timer, status, the stage being cooked, who has it, when it started, and
 * the actions.
 */
export function SessionTopBar({ session, seconds, onError }: SessionTopBarProps) {
  const { t } = useLingui();
  return (
    <div
      data-testid="session-top-bar"
      className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4 rounded-2xl border border-(--color-border) bg-(--color-surface-content) px-5 py-4"
    >
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
        <SessionTimer session={session} seconds={seconds} size="lg" />
        <SessionStatusPill tone={sessionTone(session, seconds)} />
        <Fact label={t`Stage`}>
          <span className="text-sm font-medium text-(--color-text-primary)">
            {session.currentStage?.name ?? "—"}
          </span>
        </Fact>
        <Fact label={t`Chef`}>
          <ChefLabel name={session.chefName} />
        </Fact>
        <Fact label={t`Started`}>
          <span className="text-sm font-medium text-(--color-text-primary)">{startedLabel(session)}</span>
        </Fact>
      </div>
      <SessionActions session={session} onError={onError} />
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-(--color-text-secondary)">{label}</span>
      {children}
    </div>
  );
}
